import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RepositoryService } from './../../shared/services/repository.service';
import { RiskSummary } from './../../_interfaces/RiskSummary.model';
import { Risk } from "../../_interfaces/risk.model";

import { NgbModal, ModalDismissReasons, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { MitigationEditorComponent } from "../mitigation-editor/mitigation-editor.component";
import { RiskUser } from './../../_interfaces/RiskUser.model';
import { RiskAudit } from '../../_interfaces/riskAudit.model';
import { RiskService } from 'src/app/shared/services/risk.service';
import { map, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { RiskType } from 'src/app/_interfaces/RiskType.model';
import { HeadlineRisk } from 'src/app/_interfaces/HeadlineRisk.model';
import { RiskEventEditorComponent } from '../risk-event-editor/risk-event-editor.component';

@Component({
  selector: 'app-risk-details',
  templateUrl: './risk-details.component.html',
  styleUrls: ['./risk-details.component.css']
})

export class RiskDetailsComponent implements OnInit {

  public errorMessage: string = '';
  public user: RiskUser;

  closeResult: string;
  modalOptions: NgbModalOptions;

  private loadRiskSummary: BehaviorSubject<any> = new BehaviorSubject(null);

  id$: Observable<number> = this.activeRoute.paramMap.pipe(
    map(params => +params.get('id')) 
  );

  riskSummary$: Observable<RiskSummary> = this.loadRiskSummary.pipe(
    switchMap(() => this.id$),
    switchMap((id: number) => this.getRiskSummary(id))
  );

  riskAuditEntries$: Observable<RiskAudit[]> = this.id$.pipe(
    switchMap((id: number) => this.getRiskAuditEntries(id))
  );

  risk$: Observable<Risk> = this.id$.pipe(
    switchMap((id: number) => this.riskService.getRisk(id))
  );

  riskType$: Observable<RiskType> = this.risk$.pipe(
    switchMap((risk: Risk) => 
      this.riskService.getRiskTypeDetails(risk.riskType)
    )
  );

  headlineRisk$: Observable<HeadlineRisk> = this.risk$.pipe(
    switchMap((risk: Risk) => 
      this.riskService.getHeadlineRiskDetails(risk)
    )
  );

  constructor(public repository: RepositoryService,
    private riskService: RiskService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private modalService: NgbModal) {
    this.modalOptions = {
      backdrop: 'static',
      backdropClass: 'customBackdrop'
    }
  }

  ngOnInit() {
    this.user = this.repository.GetMyUser();
  }

  private getRiskSummary(id: number): Observable<RiskSummary> {
    let apiUrl: string = `RiskSummary?id=${id}`;
  
    return this.repository.getData(apiUrl)
      .pipe(
        map(res => res as RiskSummary)
      );
  }

  private getRiskAuditEntries(id: number): Observable<RiskAudit[]> {
    let apiUrl: string = `RisksAudits/byriskid/${id}`;

    return this.repository.getData(apiUrl)
      .pipe(
        map(res => res as RiskAudit[])
      );
  }

  getProximity(prox: number) {
    let answer: string = "";
    switch (prox) {
      case 1:
        answer = 'Immediate';
        break;
      case 2:
        answer = 'Near-term (weeks)';
        break;
      case 3:
        answer = 'Mid-term (1-3 months)';
        break;
      case 4:
        answer = 'Long-term (3-9 months)';
        break;
      case 5:
        answer = 'Emergent (>9 months)';
        break;
    }
    return answer;
  }

  open() {
    const id: string = this.activeRoute.snapshot.params['id'];
    const modalRef = this.modalService.open(MitigationEditorComponent);

    modalRef.componentInstance.my_modal_title = 'Add Mitigation to';
    modalRef.componentInstance.my_risk_id = id;

    this.risk$.pipe(
      take(1)
    ).subscribe((risk: Risk) => {
      modalRef.componentInstance.costBased = risk.riskType === 3;
      modalRef.componentInstance.riskOwnerId = risk.owner;
  
      modalRef.result.then((result) => {
        this.closeResult = `Closed with: ${result}`;
        this.loadRiskSummary.next(null);
      },
        (reason) => { this.closeResult = `Dismissed ${this.getDismissReason(reason)}`; });
    });
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  public getMessageFromChild(message: string) {
    if (message === 'refresh') {
      this.loadRiskSummary.next(null);
    }
  }

  public editRiskRecord(riskId: number) {
    let riskDetailUrl: string = `/myrisks/edit-risk/${riskId}`; 
    this.router.navigate([riskDetailUrl]);
  }

  openAddRiskEvent(riskId: string, riskType: string) {
    const riskEventModal = this.modalService.open(RiskEventEditorComponent);
    riskEventModal.componentInstance.riskId = riskId;
    riskEventModal.componentInstance.riskType = riskType;

    riskEventModal.result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
      this.loadRiskSummary.next(null);
    },
      (reason) => { this.closeResult = `Dismissed ${this.getDismissReason(reason)}`; });
  }
}
